import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box, MenuItem } from "@mui/material";
import { useState, useEffect } from "react";
import { MainInput, main_button } from "./Styles";
import { useTranslation } from 'react-i18next';

const MAX_REFUND_EGP = 4000;

// showType: when true, show refund type (refund/collection). When false, only show amount.
// allowCashRefund: when false (e.g. Fincart exchanges), hide refund and force collection.
const RefundModal = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  title,
  message,
  showType = false,
  allowCashRefund = true,
  initialRefundType = "refund",
  initialRefundAmount = 0
}) => {
  const { t } = useTranslation();
  const effectiveInitialType = allowCashRefund ? initialRefundType : "collection";
  const [refundType, setRefundType] = useState(effectiveInitialType);
  const [refundAmount, setRefundAmount] = useState(initialRefundAmount);
  const modalTitle = title || t('modals.approveRequest');
  const modalMessage = message || t('modals.configureRefundDetails');

  useEffect(() => {
    if (open) {
      setRefundType(allowCashRefund ? initialRefundType : "collection");
      setRefundAmount(initialRefundAmount);
    }
  }, [open, initialRefundType, initialRefundAmount, allowCashRefund]);

  const amount = parseFloat(refundAmount || 0);
  const isRefund = (allowCashRefund ? refundType : "collection") === "refund" || !showType;
  const exceedsMaxRefund = isRefund && amount > MAX_REFUND_EGP;

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (exceedsMaxRefund) return;
    onConfirm({ refundType: allowCashRefund ? refundType : "collection", refundAmount: amount });
  };

  const canSubmit = () => {
    return refundAmount !== "" && !isNaN(amount) && !exceedsMaxRefund;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "var(--appBg)",
          color: "var(--secColor4)"
        }
      }}
    >
      <DialogTitle sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
        <Typography variant="h6" sx={{ color: "var(--secColor4)" }}>
          {modalTitle}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ color: "var(--secColor4)", mb: 2 }}>
            {modalMessage}
          </Typography>
          {showType && (
            <MainInput
              margin="normal"
              label={t('modals.type')}
              fullWidth
              select
              value={refundType}
              onChange={({ target }) => setRefundType(target.value)}
            >
              {allowCashRefund && <MenuItem value="refund">{t('modals.cashRefund')}</MenuItem>}
              <MenuItem value="collection">{t('modals.cashCollection')}</MenuItem>
            </MainInput>
          )}
          <MainInput
            margin="normal"
            type="number"
            label={t('modals.amountEGP')}
            fullWidth
            inputProps={{ min: 0, max: MAX_REFUND_EGP }}
            value={refundAmount}
            error={exceedsMaxRefund}
            helperText={exceedsMaxRefund
              ? t("returnOrder.maxRefundExceeded", { max: MAX_REFUND_EGP })
              : t("returnOrder.refundAutoCalculated")}
            onChange={({ target }) => setRefundAmount(target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, backgroundColor: "var(--appBg)" }}>
        <Button
          onClick={handleClose}
          sx={{ color: "var(--secColor4)" }}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          sx={{ ...main_button }}
          disabled={!canSubmit() || isLoading}
        >
          {isLoading ? t('common.submitting') : t('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundModal;
